import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaCheck, FaCopy, FaTimes } from 'react-icons/fa';
import {
  VISIBLE_TEST_CASE_COUNT,
  isPublicTestCaseIndex,
  publicTestIndexOrZero,
} from '../utils/visibleTestCases.ts';
import { isTabularRunValue, renderPandasRunValue } from './PandasResultView.tsx';
import { pickSqlForTestCase, sqlTestCaseInputView, type SqlDialect } from '../utils/sqlSeedTables.ts';

export type BottomPanelTab = 'tests' | 'terminal';

export type SubmissionPanelState = {
  status: string;
  message?: string;
  error?: string;
  details?: string;
  failedTestCase?: number;
  expected?: unknown;
  actual?: unknown;
  passedTests?: number;
  totalTests?: number;
  executionTime?: number;
  memoryUsage?: number;
};

type TestCaseInput = {
  input?: Record<string, unknown>;
  output?: unknown;
  expected?: unknown;
  hidden?: boolean;
  schema_sql?: string;
  schema_sql_mysql?: string;
  seed_sql?: string;
  seed_sql_mysql?: string;
};

export type ProblemBottomPanelProps = {
  activeTab: BottomPanelTab;
  onTabChange: (tab: BottomPanelTab) => void;
  submission: SubmissionPanelState | null;
  terminalContent: React.ReactNode;
  isLoading: boolean;
  loadingLabel: string;
  testCases: TestCaseInput[] | undefined;
  selectedTestIndex: number;
  onSelectTest: (index: number) => void;
  t: (key: string) => string;
  emptyPanelHint: string;
  sqlDialect?: SqlDialect;
};

const BOTTOM_PANEL_DEFAULT_HEIGHT = 240;
const BOTTOM_PANEL_MIN_HEIGHT = 120;
const BOTTOM_PANEL_MAX_HEIGHT_RATIO = 0.72;

function clampBottomPanelHeight(height: number): number {
  const maxHeight = Math.max(
    BOTTOM_PANEL_MIN_HEIGHT,
    Math.round(window.innerHeight * BOTTOM_PANEL_MAX_HEIGHT_RATIO)
  );
  return Math.min(maxHeight, Math.max(BOTTOM_PANEL_MIN_HEIGHT, Math.round(height)));
}

function isPrimitive(value: unknown): boolean {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

function isMatrix(value: unknown): value is unknown[][] {
  return Array.isArray(value) && value.length > 0 && value.every(Array.isArray);
}

function jsonCompact(value: unknown): string {
  if (Array.isArray(value) && value.every(isPrimitive)) {
    return `[${value.map((item) => jsonCompact(item)).join(', ')}]`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Компактный вывод: строка матрицы / список примитивов — в одну линию. */
function formatCompareValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (isMatrix(value)) {
    return value.map((row) => jsonCompact(row)).join('\n');
  }
  return jsonCompact(value);
}

function formatTestInputBlock(input: Record<string, unknown> | undefined): string {
  if (!input || Object.keys(input).length === 0) return '—';
  const lines: string[] = [];
  for (const [key, value] of Object.entries(input)) {
    if (isMatrix(value)) {
      lines.push(`${key} =`);
      for (const row of value) {
        lines.push(jsonCompact(row));
      }
    } else {
      const formatted = formatCompareValue(value);
      if (formatted.includes('\n')) {
        lines.push(`${key} =`);
        lines.push(formatted);
      } else {
        lines.push(`${key} = ${formatted}`);
      }
    }
  }
  return lines.join('\n');
}

function statusClass(status: string): string {
  if (status === 'Accepted') return 'accepted';
  if (status === 'Wrong Answer') return 'wrong-answer';
  return 'error-status';
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // ignore
  }
}

function ProgressRing({
  passed,
  total,
  passedLabel,
}: {
  passed: number;
  total: number;
  passedLabel: string;
}) {
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  const deg = (pct / 100) * 360;
  return (
    <div
      className="pp-panel-progress-ring"
      style={{ background: `conic-gradient(var(--nn-accent-soft) ${deg}deg, var(--nn-border) ${deg}deg)` }}
      aria-hidden
    >
      <div className="pp-panel-progress-inner">
        <span className="pp-panel-progress-fraction">
          <span className="pp-panel-progress-passed">{passed}</span>
          <span className="pp-panel-progress-total"> / {total}</span>
        </span>
        <span className="pp-panel-progress-caption">{passedLabel}</span>
      </div>
    </div>
  );
}

export function ProblemBottomPanel({
  activeTab,
  onTabChange,
  submission,
  terminalContent,
  isLoading,
  loadingLabel,
  testCases,
  selectedTestIndex,
  onSelectTest,
  t,
  emptyPanelHint,
  sqlDialect = 'postgres',
}: ProblemBottomPanelProps) {
  const [copyFlash, setCopyFlash] = useState<'expected' | 'actual' | null>(null);
  const [panelHeight, setPanelHeight] = useState(BOTTOM_PANEL_DEFAULT_HEIGHT);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartYRef = useRef(0);
  const resizeStartHeightRef = useRef(BOTTOM_PANEL_DEFAULT_HEIGHT);

  const handleResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    resizeStartYRef.current = event.clientY;
    resizeStartHeightRef.current = panelHeight;
    setIsResizing(true);
  }, [panelHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const delta = resizeStartYRef.current - event.clientY;
      const nextHeight = clampBottomPanelHeight(resizeStartHeightRef.current + delta);
      setPanelHeight(nextHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setPanelHeight((current) => clampBottomPanelHeight(current));
    };

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const totalTests = submission?.totalTests ?? testCases?.length ?? 0;
  const passedTests =
    submission?.passedTests ??
    (submission?.status === 'Accepted' ? totalTests : 0);

  const testRows = useMemo(() => {
    const n = Math.max(testCases?.length ?? 0, totalTests);
    const visibleCount = Math.min(VISIBLE_TEST_CASE_COUNT, n);
    const failedAt = submission?.failedTestCase;
    const allPassed = submission?.status === 'Accepted';
    return Array.from({ length: visibleCount }, (_, i) => {
      const num = i + 1;
      let passed: boolean | null = null;
      if (allPassed) passed = true;
      else if (failedAt != null) {
        if (num < failedAt) passed = true;
        else if (num === failedAt) passed = false;
      }
      return { index: i, num, passed };
    });
  }, [testCases, totalTests, submission?.failedTestCase, submission?.status]);

  const lastPublicIndex = Math.max(
    0,
    Math.min(VISIBLE_TEST_CASE_COUNT - 1, (testCases?.length ?? 1) - 1)
  );
  const safeIndex = Math.min(publicTestIndexOrZero(selectedTestIndex), lastPublicIndex);
  const selectedTc = testCases?.[safeIndex];
  const selectedIsHidden = selectedTc?.hidden === true || !isPublicTestCaseIndex(safeIndex);
  const selectedInput = selectedIsHidden ? undefined : selectedTc?.input;
  const selectedExpected = selectedIsHidden
    ? undefined
    : selectedTc?.expected ?? selectedTc?.output;
  const selectedSql = pickSqlForTestCase(selectedTc, sqlDialect);
  const sqlInputView = selectedIsHidden
    ? { kind: 'empty' as const }
    : sqlTestCaseInputView(selectedSql.schema, selectedSql.seed);
  const inputIsTabular =
    isTabularRunValue(selectedInput) || sqlInputView.kind === 'tables';
  const inputTablesPayload = isTabularRunValue(selectedInput)
    ? selectedInput
    : sqlInputView.kind === 'tables'
      ? sqlInputView.payload
      : undefined;

  const failedIsPublic =
    submission?.failedTestCase != null &&
    isPublicTestCaseIndex(submission.failedTestCase - 1);
  const showFailedCompare =
    failedIsPublic &&
    submission?.failedTestCase === safeIndex + 1;

  const hiddenDetails = t('problemPage.panelHiddenTestDetails');
  const expectedDisplay = selectedIsHidden
    ? hiddenDetails
    : showFailedCompare
      ? formatCompareValue(submission?.expected ?? selectedExpected)
      : formatCompareValue(selectedExpected);

  const actualDisplay = selectedIsHidden
    ? hiddenDetails
    : showFailedCompare
      ? formatCompareValue(submission?.actual)
      : submission?.status === 'Accepted'
        ? formatCompareValue(selectedExpected)
        : '—';

  const expectedRaw = selectedIsHidden
    ? undefined
    : showFailedCompare
      ? submission?.expected ?? selectedExpected
      : selectedExpected;
  const actualRaw = selectedIsHidden
    ? undefined
    : showFailedCompare
      ? submission?.actual
      : submission?.status === 'Accepted'
        ? selectedExpected
        : undefined;

  const actualMatches =
    submission?.status === 'Accepted' ||
    (showFailedCompare &&
      formatCompareValue(submission?.expected ?? selectedExpected) ===
        formatCompareValue(submission?.actual));

  const handleCopy = async (which: 'expected' | 'actual', text: string) => {
    await copyText(text);
    setCopyFlash(which);
    window.setTimeout(() => setCopyFlash(null), 1200);
  };

  const hasContent = submission != null || terminalContent != null;

  return (
    <div className="pp-bottom-panel" style={{ height: panelHeight }}>
      <div
        className={`pp-bottom-panel-resize-handle${isResizing ? ' is-dragging' : ''}`}
        role="separator"
        aria-orientation="horizontal"
        aria-label={t('problemPage.resizeBottomPanel')}
        onMouseDown={handleResizeStart}
      />
      <div className="pp-bottom-panel-tabs">
        <button
          type="button"
          className={`pp-bottom-tab ${activeTab === 'tests' ? 'active' : ''}`}
          onClick={() => onTabChange('tests')}
        >
          {t('problemPage.tabTests')}
        </button>
        <button
          type="button"
          className={`pp-bottom-tab ${activeTab === 'terminal' ? 'active' : ''}`}
          onClick={() => onTabChange('terminal')}
        >
          {t('problemPage.tabTerminal')}
        </button>
      </div>

      <div className="pp-bottom-panel-body">
        {!hasContent && !isLoading ? (
          <div className="pp-panel-empty">{emptyPanelHint}</div>
        ) : activeTab === 'terminal' ? (
          <div className="pp-panel-terminal">
            {isLoading ? (
              <div className="pp-panel-loading">{loadingLabel}</div>
            ) : (
              <div className="pp-panel-terminal-content">{terminalContent}</div>
            )}
          </div>
        ) : (
          <div className="pp-panel-tests">
            {isLoading ? (
              <div className="pp-panel-loading">{loadingLabel}</div>
            ) : submission ? (
              <>
                <section className="pp-panel-col pp-panel-summary">
                  <ProgressRing
                    passed={passedTests}
                    total={totalTests || passedTests || 1}
                    passedLabel={t('problemPage.testsPassedCaption')}
                  />
                  <dl className="pp-panel-stats">
                    <div className="pp-panel-stat-row">
                      <dt>{t('problemPage.panelStatus')}</dt>
                      <dd className={statusClass(submission.status)}>
                        {submission.status === 'Accepted'
                          ? t('problemPage.statusAccepted')
                          : submission.status === 'Wrong Answer'
                            ? t('problemPage.statusWrongAnswer')
                            : submission.status}
                      </dd>
                    </div>
                    <div className="pp-panel-stat-row">
                      <dt>{t('problemPage.panelTime')}</dt>
                      <dd>
                        {submission.executionTime != null
                          ? `${submission.executionTime} ${t('problemPage.panelTimeMs')}`
                          : '—'}
                      </dd>
                    </div>
                    <div className="pp-panel-stat-row">
                      <dt>{t('problemPage.panelMemory')}</dt>
                      <dd>
                        {submission.memoryUsage != null
                          ? `${submission.memoryUsage} ${t('problemPage.panelMemoryMb')}`
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                  {(submission.message || submission.error || submission.details) &&
                    submission.status !== 'Wrong Answer' &&
                    submission.status !== 'Accepted' && (
                    <p className="pp-panel-message">
                      {submission.message || submission.error}
                      {submission.details ? (
                        <>
                          <br />
                          <span className="pp-panel-details">{submission.details}</span>
                        </>
                      ) : null}
                    </p>
                  )}
                </section>

                <section className="pp-panel-col pp-panel-test-list">
                  <h4 className="pp-panel-col-title">{t('problemPage.panelTestSet')}</h4>
                  <ul className="pp-panel-tests-ul">
                    {testRows.map((row) => (
                      <li key={row.index}>
                        <button
                          type="button"
                          className={`pp-panel-test-item${safeIndex === row.index ? ' selected' : ''}`}
                          onClick={() => onSelectTest(row.index)}
                        >
                          <span className="pp-panel-test-name">
                            {t('problemPage.testCaseLabel')} {row.num}
                          </span>
                          <span className="pp-panel-test-meta">
                            {row.passed === true && (
                              <FaCheck className="pp-panel-icon-pass" aria-hidden />
                            )}
                            {row.passed === false && (
                              <FaTimes className="pp-panel-icon-fail" aria-hidden />
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="pp-panel-col pp-panel-input-col">
                  <h4 className="pp-panel-col-title">{t('problemPage.panelInputData')}</h4>
                  {selectedIsHidden ? (
                    <pre className="pp-panel-code-block">{hiddenDetails}</pre>
                  ) : inputIsTabular && inputTablesPayload != null ? (
                    <div className="pp-panel-input-tables">
                      {renderPandasRunValue(inputTablesPayload)}
                    </div>
                  ) : (
                    <pre className="pp-panel-code-block">
                      {sqlInputView.kind === 'sql'
                        ? sqlInputView.text
                        : formatTestInputBlock(selectedInput)}
                    </pre>
                  )}
                </section>

                <section className="pp-panel-col pp-panel-compare-col">
                  <div className={`pp-panel-compare-box${isTabularRunValue(expectedRaw) ? ' pp-panel-compare-box--tables' : ''}`}>
                    <div className="pp-panel-compare-head">
                      <span>{t('problemPage.panelExpectedOutput')}</span>
                      <button
                        type="button"
                        className="pp-panel-copy-btn"
                        title={t('problemPage.panelCopy')}
                        disabled={selectedIsHidden}
                        onClick={() => void handleCopy('expected', expectedDisplay)}
                        aria-label={t('problemPage.panelCopy')}
                      >
                        <FaCopy />
                      </button>
                    </div>
                    {!selectedIsHidden && isTabularRunValue(expectedRaw) ? (
                      <div className="pp-panel-compare-tables">
                        {renderPandasRunValue(expectedRaw)}
                      </div>
                    ) : (
                      <pre className="pp-panel-compare-value">{expectedDisplay}</pre>
                    )}
                    {copyFlash === 'expected' && (
                      <span className="pp-panel-copied">{t('problemPage.conditionCopied')}</span>
                    )}
                  </div>
                  <div className={`pp-panel-compare-box${isTabularRunValue(actualRaw) ? ' pp-panel-compare-box--tables' : ''}`}>
                    <div className="pp-panel-compare-head">
                      <span>{t('problemPage.panelYourOutput')}</span>
                      <button
                        type="button"
                        className="pp-panel-copy-btn"
                        title={t('problemPage.panelCopy')}
                        disabled={selectedIsHidden}
                        onClick={() => void handleCopy('actual', actualDisplay)}
                        aria-label={t('problemPage.panelCopy')}
                      >
                        <FaCopy />
                      </button>
                    </div>
                    {!selectedIsHidden && isTabularRunValue(actualRaw) ? (
                      <div
                        className={`pp-panel-compare-tables ${actualMatches ? 'match' : 'mismatch'}`}
                      >
                        {renderPandasRunValue(actualRaw)}
                      </div>
                    ) : (
                      <pre
                        className={`pp-panel-compare-value ${actualMatches ? 'match' : 'mismatch'}`}
                      >
                        {actualDisplay}
                      </pre>
                    )}
                    {copyFlash === 'actual' && (
                      <span className="pp-panel-copied">{t('problemPage.conditionCopied')}</span>
                    )}
                  </div>
                </section>
              </>
            ) : (
              <div className="pp-panel-empty">{emptyPanelHint}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
